import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  DiscountRuleRequest,
  DiscountRuleResponse,
  DiscountRuleSearchParams,
  ApplyCouponRequest,
  ApplyCouponResponse,
  ResolveDiscountResponse,
  verifyCouponResponse,
} from '../models/discount-rule.model';
// Inc 28: el timeline reutiliza el shape de auditoría y la página estilo audit
// ({ content, page, size, totalElements, totalPages, hasNext, hasPrevious }).
import {
  AuditLogResponse,
  PageResponse as TimelinePageResponse,
} from '../../audit/models/audit.model';

@Injectable({
  providedIn: 'root',
})
export class DiscountRuleApiService {
  private readonly apiUrl = `${environment.apiUrl}api/product-discount-rules`;

  constructor(private http: HttpClient) {}

  searchDiscountRule(
    params: DiscountRuleSearchParams,
  ): Observable<DiscountRuleResponse[]> {
    let httpParams = new HttpParams().set(
      'businessId',
      params.businessId.toString(),
    );
    if (params.ruleType)
      httpParams = httpParams.set('ruleType', params.ruleType);
    if (params.productId !== undefined)
      httpParams = httpParams.set('productId', params.productId.toString());
    if (params.contactId !== undefined)
      httpParams = httpParams.set('contactId', params.contactId.toString());
    if (params.couponCode)
      httpParams = httpParams.set('couponCode', params.couponCode);
    if (params.isActive !== undefined)
      httpParams = httpParams.set('isActive', params.isActive.toString());
    return this.http.get<DiscountRuleResponse[]>(this.apiUrl, {
      params: httpParams,
    });
  }

  getDiscountRuleById(id: number): Observable<DiscountRuleResponse> {
    return this.http.get<DiscountRuleResponse>(`${this.apiUrl}/${id}`);
  }

  getApplicableDiscountRules(
    productId: number,
    businessId: number,
  ): Observable<DiscountRuleResponse[]> {
    const params = new HttpParams()
      .set('productId', productId.toString())
      .set('businessId', businessId.toString());
    return this.http.get<DiscountRuleResponse[]>(`${this.apiUrl}/applicable`, {
      params,
    });
  }

  createDiscountRule(
    req: DiscountRuleRequest,
  ): Observable<DiscountRuleResponse> {
    return this.http.post<DiscountRuleResponse>(this.apiUrl, req);
  }

  updateDiscountRule(
    id: number,
    req: DiscountRuleRequest,
  ): Observable<DiscountRuleResponse> {
    return this.http.put<DiscountRuleResponse>(`${this.apiUrl}/${id}`, req);
  }

  deleteDiscountRule(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { params });
  }

  /**
   * Inc 28: timeline propio de la regla de descuento (products:VIEW, ya NO el genérico
   * de auditoría que exigía audit:VIEW y devolvía 403 en silencio).
   * GET /api/product-discount-rules/{id}/timeline
   */
  getDiscountRuleTimeline(
    id: number,
    page: number,
    size: number,
  ): Observable<TimelinePageResponse<AuditLogResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<TimelinePageResponse<AuditLogResponse>>(
      `${this.apiUrl}/${id}/timeline`,
      { params },
    );
  }

  verifyCoupon(couponCode: string) {
    const params = new HttpParams().set('couponCode', couponCode.toString());
    return this.http.get<verifyCouponResponse>(`${this.apiUrl}/verify-coupon`, {
      params,
    });
  }

  applyCoupon(req: ApplyCouponRequest): Observable<ApplyCouponResponse> {
    return this.http.post<ApplyCouponResponse>(
      `${this.apiUrl}/apply-coupon`,
      req,
    );
  }

  /**
   * Resuelve el mejor descuento vigente para un producto/cantidad/precio unitario (Inc 40).
   * GET /api/product-discount-rules/resolve?productId=&quantity=&unitPrice=[&contactId=]
   * El backend toma el businessId del JWT; se envía primero por convención multi-tenant.
   * Si no aplica ninguna regla → applied=false, discountAmount=0, finalTotal=lineTotal.
   *
   * (Inc 32) `contactId` es OPCIONAL y retrocompatible: si se omite solo se consideran las reglas
   * generales; si se envía, además se consideran las reglas CONTRACTUALES de ese contacto. Solo se
   * auto-aplican reglas 'volume' y 'contractual' ('coupon' va por apply-coupon; 'min_price' es piso).
   */
  resolveDiscount(
    productId: number,
    quantity: number,
    unitPrice: number,
    businessId: number,
    contactId?: number,
  ): Observable<ResolveDiscountResponse> {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('productId', productId.toString())
      .set('quantity', quantity.toString())
      .set('unitPrice', unitPrice.toString());
    if (contactId !== undefined)
      params = params.set('contactId', contactId.toString());
    return this.http.get<ResolveDiscountResponse>(`${this.apiUrl}/resolve`, {
      params,
    });
  }
}
